import React from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import Button from 'components/Button/Button';
import FormItem from 'components/FormItem/FormItem';
import IconButton from 'components/IconButton/IconButton';
import Input from 'components/Input/Input';

import ArrowBack24 from 'icons/ArrowBack24';

import useApi from 'utils/api';
import classNames from 'utils/classNames';
import { validateEmail, validateName, validatePassword } from 'utils/validate';

import styles from './auth.scss';

export default function Register() {
    const api = useApi();
    const navigate = useNavigate();
    const { t } = useTranslation();

    const [step, setStep] = React.useState('email');
    const [name, setName] = React.useState({ value: '', error: null });
    const [email, setEmail] = React.useState({ value: '', error: null });
    const [password, setPassword] = React.useState({ value: '', error: null });
    const [passwordRepeat, setPasswordRepeat] = React.useState({ value: '', error: null });
    const [loading, setLoading] = React.useState(false);
    const [registerError, setRegisterError] = React.useState(null);

    const stepLayout = {
        email: {
            title: t('welcome.register.step.email.title'),
            caption: t('welcome.register.step.email.description'),
            actionLabel: t('welcome.register.step.email.action'),
        },
        password: {
            title: t('welcome.register.step.password.title'),
            caption: t('welcome.register.step.password.description'),
            actionLabel: t('welcome.main.signUp'),
        },
    };

    function handleNameChange(e) {
        const value = e.target.value;
        const error = validateName(value);
        setName({ value, error });
    }

    /**
     * Saves email password to state and reset errors.
     * @param {event} e Event triggered on email input.
     */
    function handleEmailChange(e) {
        const value = e.target.value.replace(/\s/ig, '');
        const error = null;
        setEmail(email => ({ ...email, value, error }));
    }

    /**
     * Validates password entered by user.
     * @param {event} e Event triggered on password value changes
     */
    function handlePasswordChange(e) {
        const value = e.target.value.replace(/\s/ig, '');
        const error = validatePassword(value);
        setPassword(password => ({ ...password, value, error }));
    }

    /**
     * Validates password repeat entered by user.
     * @param {event} e Event triggered on password value changes
     */
    function handleRepeatPasswordChange(e) {
        const value = e.target.value.replace(/\s/ig, '');
        const error = null;
        setPasswordRepeat(password => ({ ...password, value, error }));
    }

    /**
     * Validate Email and go to the next step.
     * @param {event} e KeyDown event.
     */
    function handleEmailKeyDown(e) {
        if (e.which === 13) {
            if (e.target.name === 'email' || e.target.name === 'password') {
                handleActionClick();
            }
        }
    }

    /**
     * Go back to email form.
     */
    function handleBackClick() {
        if (step === 'email') {
            navigate('/');
        } else {
            setStep('email');
        }
    }

    /**
     * Validate email and proceed to password enter step.
     */
    function handleActionClick() {
        switch (step) {
        case 'email':
            checkEmail();
            break;
        case 'password':
            register();
            break;
        default:
            return false;
        }
    }

    /**
     * Check if email is valid and available.
     */
    function checkEmail() {
        if (email.error || name.error) return;

        const error = validateEmail(email.value);
        setEmail(email => ({ ...email, error }));

        const nameError = validateName(name.value);
        setName(prevName => ({ ...prevName, error: nameError }));

        // Check email for availability.
        if (error === null && nameError === null) {
            setLoading(true);
            api.checkEmail(email.value).then(() => {
                setStep('password');
                setLoading(false);
            }).catch(error => {
                console.error(error);
                setEmail(email => ({ ...email, error: new Error('Этот Email уже используется.') }));
                setLoading(false);
            });
        }
    }

    /**
     * Try to authorize user and redirect to workspace.
     */
    function register() {
        // Validate password.
        const error = validatePassword(password.value);

        if (error !== null) {
            setPassword(password => ({ ...password, error }));

            return;
        }

        // If passwords are not equals.
        if (password.value.toString() !== passwordRepeat.value.toString()) {
            const error = new Error('Пароли не совпадают');
            setPasswordRepeat(passwordRepeat => ({ ...passwordRepeat, error }));

            return;
        }

        // Prepare credentials and register.
        const credentials = {
            email: email.value,
            password: password.value,
            name: name.value,
        };

        setLoading(true);

        api.register(credentials)
            .then()
            .catch(error => {
                console.error(error);
                setRegisterError(new Error('Не удалось создать новый аккаунт. Возможно, email уже занят.'));
            })
            .finally(() => {
                setTimeout(() => setLoading(false), 500);
            });
    }

    /**
     * Check if action button of current step is disabled.
     * @returns {boolean} Action button disabled or not.
     */
    function isActionDisabled() {
        switch (step) {
        case 'email':
            return email.error !== null || email.value === '';
        case 'password':
            return passwordRepeat.error !== null || password.error !== null || passwordRepeat.value === '';
        default:
            return false;
        }
    }

    return (
        <div className={styles.sign}>
            <div className={styles.top + ' ' + styles.flexStart} style={{ paddingBottom: 0 }}>
                <div className={styles.back}>
                    <IconButton icon={<ArrowBack24 />} onClick={handleBackClick} />
                </div>
                <h2>{stepLayout[step].title}</h2>
                <p className={classNames(styles.text, {
                    [styles.center]: true,
                    [styles.error]: registerError !== null,
                })}>{registerError === null ? stepLayout[step].caption : registerError.message}</p>
                <div className={styles.authForm}>
                    {step === 'email' &&
                        <>
                            <FormItem style={{ padding: 0 }} error={name.error}>
                                <Input
                                    onKeyDown={handleEmailKeyDown}
                                    placeholder={'Ваше имя'}
                                    value={name.value}
                                    type={'text'}
                                    name={'name'}
                                    onChange={handleNameChange}
                                />
                            </FormItem>
                            <FormItem style={{ padding: '1rem 0 0 0' }} error={email.error}>
                                <Input
                                    onKeyDown={handleEmailKeyDown}
                                    placeholder={'Ваш Email'}
                                    value={email.value}
                                    type={'text'}
                                    name={'email'}
                                    onChange={handleEmailChange}
                                />
                            </FormItem>
                        </>
                    }
                    {step === 'password' &&
                        <>
                            <FormItem style={{ padding: 0 }} error={password.error}>
                                <Input
                                    placeholder={'Пароль'}
                                    value={password.value}
                                    name={'password'}
                                    type={'password'}
                                    onChange={handlePasswordChange}
                                />
                            </FormItem>
                            <FormItem style={{ padding: 0, paddingTop: 12 }} error={passwordRepeat.error}>
                                <Input
                                    placeholder={'Повторите пароль'}
                                    value={passwordRepeat.value}
                                    name={'password-repeat'}
                                    type={'password'}
                                    onChange={handleRepeatPasswordChange}
                                />
                            </FormItem>
                        </>
                    }
                </div>
            </div>
            <div className={styles.bottom}>
                <Button
                    text={stepLayout[step].actionLabel}
                    loading={loading}
                    fill={true}
                    disabled={isActionDisabled()}
                    onClick={handleActionClick}
                />
            </div>
        </div>
    );
};
